import { z } from 'zod';

import { BorrowerId, DocumentId, PaymentId, PayoffId, PrincipalModificationId, RepayAccountId } from '../BrandedIds';
import {
  BaseEmailTemplateType,
  BorrowerInsuranceExpiredEmail,
  BorrowerInsuranceExpiringSoonEmail,
  DocumentEmailTemplateType,
  GoodbyeBeforeWelcome,
  GoodbyeEmail,
  PaidInFullStatementEmail,
  PartialPayoffConfirmationEmail,
  PaymentEmailTemplateType,
  PaymentFailedAutopayEmail,
  PrincipalModificationAppliedEmail,
  PrincipalModificationCancelledEmail,
  RescindGoodbyeEmail,
  UpdateAutopayEmailTemplateType,
} from '../emails/Templates';
import { SendEmailOptions } from '../events/SendEmailEvent';
import { BorrowerInsuranceExpirationInformation } from '../loan/BorrowerInsuranceExpirationInformation';
import { LoanStatus } from '../loan/LoanStatus';
import { BaseLoanIdSideEffect } from './BaseLoanIdSideEffect';

const SendBaseEmailSideEffectContext = BaseLoanIdSideEffect.extend({
  borrowerIds: z.array(BorrowerId),
  emailTemplate: BaseEmailTemplateType,
  opts: SendEmailOptions,
});
export const SendEmailSideEffect = SendBaseEmailSideEffectContext.extend({
  type: z.literal('sendEmail'),
});

const SendRescindGoodbyeEmailSideEffectContext = SendBaseEmailSideEffectContext.extend({
  emailTemplate: RescindGoodbyeEmail,
  transferEffectiveDate: z.string(),
  newLenderName: z.string(),
  newLoanStatus: LoanStatus,
});
const SendRescindGoodbyeEmailSideEffect = SendRescindGoodbyeEmailSideEffectContext.extend({
  type: z.literal('sendEmail'),
});
export type SendRescindGoodbyeEmailSideEffect = z.infer<typeof SendRescindGoodbyeEmailSideEffect>;

const SendUpdateableEmailSideEffectContext = SendBaseEmailSideEffectContext.extend({
  emailTemplate: z.union([GoodbyeEmail, GoodbyeBeforeWelcome, PaidInFullStatementEmail]),
  isUpdate: z.boolean().optional(),
});
const SendUpdateableEmailSideEffect = SendUpdateableEmailSideEffectContext.extend({
  type: z.literal('sendEmail'),
});
export type SendUpdateableEmailSideEffect = z.infer<typeof SendUpdateableEmailSideEffect>;

const SendPaymentEmailSideEffectContext = SendBaseEmailSideEffectContext.extend({
  emailTemplate: PaymentEmailTemplateType,
  paymentId: PaymentId,
});
export const SendPaymentEmailSideEffect = SendPaymentEmailSideEffectContext.extend({
  type: z.literal('sendEmail'),
});
export type SendPaymentEmailSideEffect = z.infer<typeof SendPaymentEmailSideEffect>;

const SendPaymentFailedEmailSideEffectContext = SendBaseEmailSideEffectContext.extend({
  emailTemplate: PaymentFailedAutopayEmail,
  paymentId: PaymentId.optional(),
  failureReason: z.string().optional(),
});
export const SendPaymentFailedEmailSideEffect = SendPaymentFailedEmailSideEffectContext.extend({
  type: z.literal('sendEmail'),
});
export type SendPaymentFailedEmailSideEffect = z.infer<typeof SendPaymentFailedEmailSideEffect>;

const DwollaUpdateAutopayEmailSideEffectContext = SendBaseEmailSideEffectContext.extend({
  paymentProvider: z.literal('dwolla'),
  emailTemplate: UpdateAutopayEmailTemplateType,
  autopayBankUrl: z.string(),
});
const UsioUpdateAutopayEmailSideEffectContext = SendBaseEmailSideEffectContext.extend({
  paymentProvider: z.literal('usio'),
  emailTemplate: UpdateAutopayEmailTemplateType,
  confirmationId: z.string(),
});
const RepayUpdateAutopayEmailSideEffectContext = SendBaseEmailSideEffectContext.extend({
  paymentProvider: z.literal('repay'),
  emailTemplate: UpdateAutopayEmailTemplateType,
  accountId: RepayAccountId,
});

export const SendUpdateAutopayEmailSideEffectContext = z.discriminatedUnion('paymentProvider', [
  DwollaUpdateAutopayEmailSideEffectContext,
  UsioUpdateAutopayEmailSideEffectContext,
  RepayUpdateAutopayEmailSideEffectContext,
]);

const DwollaUpdateAutopayEmailSideEffect = DwollaUpdateAutopayEmailSideEffectContext.extend({
  type: z.literal('sendEmail'),
});

const UsioUpdateAutopayEmailSideEffect = UsioUpdateAutopayEmailSideEffectContext.extend({
  type: z.literal('sendEmail'),
});

const RepayUpdateAutopayEmailSideEffect = RepayUpdateAutopayEmailSideEffectContext.extend({
  type: z.literal('sendEmail'),
});

export const SendUpdateAutopayEmailSideEffect = z.discriminatedUnion('paymentProvider', [
  DwollaUpdateAutopayEmailSideEffect,
  UsioUpdateAutopayEmailSideEffect,
  RepayUpdateAutopayEmailSideEffect,
]);
export type SendUpdateAutopayEmailSideEffect = z.infer<typeof SendUpdateAutopayEmailSideEffect>;

const SendDocumentEmailSideEffectContext = SendBaseEmailSideEffectContext.extend({
  emailTemplate: DocumentEmailTemplateType,
  documentId: DocumentId,
});
export const SendDocumentEmailSideEffect = SendDocumentEmailSideEffectContext.extend({
  type: z.literal('sendEmail'),
});
export type SendDocumentEmailSideEffect = z.infer<typeof SendDocumentEmailSideEffect>;

const SendPartialPayoffConfirmationEmailSideEffectContext = SendBaseEmailSideEffectContext.extend({
  emailTemplate: PartialPayoffConfirmationEmail,
  payoffId: PayoffId,
});
export const SendPartialPayoffConfirmationEmailSideEffect = SendPartialPayoffConfirmationEmailSideEffectContext.extend({
  type: z.literal('sendEmail'),
});
export type SendPartialPayoffConfirmationEmailSideEffect = z.infer<typeof SendPartialPayoffConfirmationEmailSideEffect>;

const SendPrincipalModificationAppliedEmailSideEffectContext = SendBaseEmailSideEffectContext.extend({
  emailTemplate: PrincipalModificationAppliedEmail,
  principalModificationId: PrincipalModificationId,
});
export const SendPrincipalModificationAppliedEmailSideEffect =
  SendPrincipalModificationAppliedEmailSideEffectContext.extend({
    type: z.literal('sendEmail'),
  });

export type SendPrincipalModificationAppliedEmailSideEffect = z.infer<
  typeof SendPrincipalModificationAppliedEmailSideEffect
>;

const SendPrincipalModificationCancelledEmailSideEffectContext = SendBaseEmailSideEffectContext.extend({
  emailTemplate: PrincipalModificationCancelledEmail,
  principalModificationId: PrincipalModificationId,
});

export const SendPrincipalModificationCancelledEmailSideEffect =
  SendPrincipalModificationCancelledEmailSideEffectContext.extend({
    type: z.literal('sendEmail'),
  });

export type SendPrincipalModificationCancelledEmailSideEffect = z.infer<
  typeof SendPrincipalModificationCancelledEmailSideEffect
>;

const SendBorrowerInsuranceExpiringSoonEmailSideEffectContext = SendBaseEmailSideEffectContext.extend({
  emailTemplate: BorrowerInsuranceExpiringSoonEmail,
  insuranceExpirationInformation: BorrowerInsuranceExpirationInformation,
});
export const SendBorrowerInsuranceExpiringSoonEmailSideEffect =
  SendBorrowerInsuranceExpiringSoonEmailSideEffectContext.extend({
    type: z.literal('sendEmail'),
  });
export type SendBorrowerInsuranceExpiringSoonEmailSideEffect = z.infer<
  typeof SendBorrowerInsuranceExpiringSoonEmailSideEffect
>;

const SendBorrowerInsuranceExpiredEmailSideEffectContext = SendBaseEmailSideEffectContext.extend({
  emailTemplate: BorrowerInsuranceExpiredEmail,
  insuranceExpirationInformation: BorrowerInsuranceExpirationInformation,
});
export const SendBorrowerInsuranceExpiredEmailSideEffect = SendBorrowerInsuranceExpiredEmailSideEffectContext.extend({
  type: z.literal('sendEmail'),
});
export type SendBorrowerInsuranceExpiredEmailSideEffect = z.infer<typeof SendBorrowerInsuranceExpiredEmailSideEffect>;

export const SendEmailSideEffectContext = z.union([
  SendBaseEmailSideEffectContext,
  SendPaymentEmailSideEffectContext,
  SendPaymentFailedEmailSideEffectContext,
  SendRescindGoodbyeEmailSideEffectContext,
  SendUpdateAutopayEmailSideEffectContext,
  SendDocumentEmailSideEffectContext,
  SendUpdateableEmailSideEffectContext,
  SendPartialPayoffConfirmationEmailSideEffectContext,
  SendPrincipalModificationAppliedEmailSideEffectContext,
  SendPrincipalModificationCancelledEmailSideEffectContext,
  SendBorrowerInsuranceExpiringSoonEmailSideEffectContext,
  SendBorrowerInsuranceExpiredEmailSideEffectContext,
]);
export type SendEmailSideEffect = z.infer<typeof SendEmailSideEffect>;
